import {
  AfterViewInit,
  Component,
  OnInit,
  computed,
  effect,
  inject,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ActivatedRoute,
  Router,
  RouterModule,
} from '@angular/router';
import {
  toSignal,
} from '@angular/core/rxjs-interop';
import { PropertiesService } from '../../data-access/properties.service';
import {
  from,
  map,
  mergeMap,
  switchMap,
  tap,
} from 'rxjs';
import { FeatureLayersService } from 'src/app/map/features/property/features/feature-layers/data-access/feature-layers.service';
import { MapService } from 'src/app/map/data-access/map.service';
import { SidebarService } from '../../../sidebar/data-access/sidebar.service';
import { ModalService } from 'src/app/@core/services/modal/modal.service';
import { ToastService } from 'src/app/@core/services/toast/toast.service';
import { TuiLoader } from '@taiga-ui/core';
import { TuiTextfield } from '@taiga-ui/core';
import {
  TUI_DEFAULT_INPUT_COLORS,
  TuiInputColorModule,
} from '@taiga-ui/legacy';
import { CreatePropertyViewComponent } from '../CreatePropertyView/CreatePropertyView.component';
import { PropertyViewService } from '../../data-access/PropertyViewService.service';
import { TuiDataList } from '@taiga-ui/core';
import { TuiDataListWrapper, tuiItemsHandlersProvider } from '@taiga-ui/kit';
import {
  TuiSelectModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/legacy';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { DataCatalogueService } from '../../../data-catalogue/data-access/services/data-catalogue.service';
import { TuiSkeleton } from '@taiga-ui/kit';
import { PropertyViewDTO } from '../../data-access/models/property-view-model';
import { PropertiesState } from '../../data-access/state';
import { ActionPortalDirective } from 'src/app/shared/features/action-portal/directives/action-portal.directive';
import { filterNil } from 'ngxtension/filter-nil';
import { TooltipDirective } from 'src/app/shared/directives/tooltip/tooltip.directive';
import {
  SelectModel,
  SelectComponent,
} from 'src/app/shared/features/select/select.component';
import { mapArray } from 'ngxtension/map-array';
import { debug } from 'ngxtension/debug';
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON";
import {Fill, Stroke, Style, Text} from "ol/style";
import LayerGroup from "ol/layer/Group";

@Component({
  selector: 'app-property-details',
  imports: [
    CommonModule,
    RouterModule,
    TuiLoader,
    TuiTextfield,
    TuiInputColorModule,
    MatFormFieldModule,
    MatSelectModule,
    TooltipDirective,
    TuiSelectModule,
    TuiDataListWrapper,
    TuiSkeleton,
    TuiDataList,
    TuiTextfieldControllerModule,
    ReactiveFormsModule,
    ActionPortalDirective,
    SelectComponent,
  ],
  templateUrl: './property-details.component.html',
  styleUrl: './property-details.component.css',
  providers: [
    tuiItemsHandlersProvider({
      stringify: (item: PropertyViewDTO) => `${item.name}`,
    }),
  ],
})
export class PropertyDetailsComponent implements OnInit, AfterViewInit {
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly propertiesService: PropertiesService =
    inject(PropertiesService);

  public readonly propertiesState = inject(PropertiesState);
  private readonly mapService: MapService = inject(MapService);
  private readonly featureLayerService: FeatureLayersService =
    inject(FeatureLayersService);
  private readonly catalogueService: DataCatalogueService =
    inject(DataCatalogueService);
  private readonly sidebarService: SidebarService = inject(SidebarService);

  private readonly modalService: ModalService = inject(ModalService);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly propertyViewService: PropertyViewService =
    inject(PropertyViewService);
  private routeParams = toSignal(this.route.params, {});
  public propertyID = computed<string>(() =>
    this.routeParams()['id']
      ? this.routeParams()['id'] == ':id'
        ? ''
        : this.routeParams()['id']
      : ''
  );

  protected readonly palette = TUI_DEFAULT_INPUT_COLORS;

  public viewControl = new FormControl<string>(null);

  viewControlValue = toSignal(
    this.viewControl.valueChanges.pipe(map(() => this.viewControl.value)),
    { initialValue: null }
  );

  public isZooming = toSignal(this.mapService.isZooming);

  views = toSignal(
    this.propertyViewService.propertyViews$.pipe(
      mapArray<PropertyViewDTO, SelectModel>((item) => ({
        id: item.propertyViewID,
        displayName: item.name,
      }))
    ),
    { initialValue: [] }
  );

  viewControlDisabled = computed(() => {
    return this.viewControlValue() == null ? true : false;
  });

  currentlyAddedLayers = toSignal(this.catalogueService.addedLayers$);

  public property = this.propertiesState.activeProperty;

  public sidebarStatus = toSignal(this.sidebarService.opened$);

  validID;

  constructor() {
    this.viewControl.valueChanges
      .pipe(
        debug('Property View'),
        tap((view) => {
          if (view == null) {
            return;
          }
          this.currentlyAddedLayers().forEach((catalogueLayer) =>
            catalogueLayer.layer.setVisible(false)
          );
        }),
        filterNil(),
        switchMap((selectedView) =>
          this.propertyViewService.propertyViews$.pipe(
            map((propertyViews) => ({ selectedView, propertyViews }))
          )
        ),
        map(({ selectedView, propertyViews }) =>
          propertyViews.find(
            (propertyView) => propertyView.propertyViewID === selectedView
          )
        ),
        filterNil(),
        map((view) => view.layers),
        switchMap((layers) =>
          from(layers).pipe(
            mergeMap((layer) =>
              this.catalogueService
                .loadLayerByID(layer.dataCatalogueLayerID)
                .pipe(
                  tap((layerData) =>
                    this.catalogueService.addLayerToMap(layerData, {
                      visible: layer.isVisible,
                      opacity: layer.opacity,
                    })
                  )
                )
            )
          )
        )
      )
      .subscribe();

    this.validID = computed(() => (this.propertyID() == '' ? false : true));

    effect(
      () => {
        if (!this.validID()) {
          this.router.navigate(['/map/property']);

          return;
        }

        const propertyID = this.propertyID();

        untracked(() => {
          this.propertiesState.setActivePropertyID(propertyID);

          this.propertyViewService.getPropertyViews(propertyID).subscribe();
          if (this.sidebarStatus()) {
            return;
          }

          this.sidebarService.open();
        });
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    if (!this.validID()) {
      this.router.navigate(['/map/property']);

      return;
    }

    const propertyID = this.propertyID();

    this.propertiesState.setActivePropertyID(propertyID);

    this.propertyViewService.getPropertyViews(propertyID).subscribe();

    if (untracked(() => this.sidebarStatus())) {
      return;
    }

    this.sidebarService.open();
  }

  onClick() {
    this.router.navigate([`/map/property`, this.propertyID(), 'digitize']);
  }

  zoomTo() {
    this.mapService.zoomToFeature(
      this.propertiesState.activeProperty().boundaryFeature
    );
  }

  getPropertyCadastre() {
    const propGeom = this.propertiesState.activeProperty().geom
    const propId = this.propertiesState.activeProperty().propertyID

    if (!propGeom) {
      return
    }
    this.propertiesService.getPropertyCadastre(propId).subscribe(cadastre => {
      const features = new GeoJSON().readFeatures(cadastre, {
        dataProjection: 'EPSG:3857',
        featureProjection: 'EPSG:3857'
      });

      const vectorLayer = new VectorLayer({
        source: new VectorSource(),
        // Optional: Define custom appearance
        style: (feature) => {
          return new Style({
            fill: new Fill({
              color: 'rgba(255,255,255,0.01)',
            }),
            text: new Text({
              font: '12px Calibri,sans-serif',
              fill: new Fill({ color: '#000' }),
              stroke: new Stroke({ color: '#fff', width: 2 }),
              text: feature.get('parcel_id') ?? '',
            }),
            stroke: new Stroke({
              color: 'red',
              width: 2.5
            }),
          });
        },
        zIndex: 1001
      });
      const targetGroup = this.mapService.findLayerByName('Property Layers')

      vectorLayer.set('title', 'Property Cadastre');
      vectorLayer.set('sourceType', 'cadastre');
      vectorLayer.setVisible(true);

      const source = vectorLayer.getSource();
      source?.clear();
      source?.addFeatures(features);

      targetGroup.getLayers().push(vectorLayer);

    });


    // const selectedParcels =  source.getFeaturesInExtent(mapExtent)
    //   .filter(parcel => {
    //     const parcelGeom = parcel.getGeometry();
    //     if (!parcelGeom) return false;
    //
    //     // Fast bounding-box check
    //     return parcelGeom.intersectsExtent(extent);
    //   })
    //   .map(parcel => parcel.clone());
  }

  createView() {
    const instance = this.modalService.showComponent(
      CreatePropertyViewComponent,
      { propertyID: this.propertyID() }
    );

    instance.componentInstance.submit.subscribe((result) => {
      instance.close();

      if (result.err) {
        return;
      }

      const model = result.unwrap();
      this.propertyViewService.createPropertyView(model).subscribe();
    });
  }

  export(propertyID) {
    this.modalService
      .showDownloadLayer()
      .pipe(
        switchMap((data) => {
          return this.propertiesService.exportSpatialData(
            propertyID,
            data.format
          );
        })
      )
      .subscribe((response: any) => {
        const contentType =
          response.headers.get('Content-Type') || 'application/octet-stream';

        const blob = new Blob([response.body], {
          type: contentType,
        });

        const contentDisposition = response.headers.get('Content-Disposition');

        let filename = 'download.json';
        if (contentDisposition) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
            contentDisposition
          );
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '').trim();
          }
        }

        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        anchor.click();
        window.URL.revokeObjectURL(url);
      });
  }
}
